import React, { useMemo } from 'react';
// redux
import { useAppSelector } from '../../app/hooks';
import { useTheme } from '@mui/material/styles';
// interface
import { Badge, Company } from '../../interfaces/users.interface';
import { Survey } from '../../interfaces/survey.interface';
import { AssessmentSurvey, AssessorAnswer } from '../../interfaces/assessment.interface';
// services
import { MILESTONE_STATUS, assessmentDisplayLogic, getRemediationCounts, getRemediationCountsFromStatusObject, surveyDisplayLogic } from '../../services/surveyHelpers';
// components
import { AssessmentSurveyAuditLogs } from '../Assessments/AssessmentAuditLog';
import { Workflow, WorkflowDisplayStatus } from '../../interfaces/workflow.interface';
import { Box, LinearProgress, Typography } from '@mui/material';
import { USER_TYPES } from '../../services/user';
import { useWorkflowMap } from '../../hooks/useWorkflowMap';
import { GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL } from '../../services/badge';

type WorkflowStatusProps = {
  workflow?: Workflow;
  company: Company;
  survey?: Survey;
  assessmentSurvey?: AssessmentSurvey;
  hasBlueExpired?: boolean;
  hasGoldExpired?: boolean;
  hideBallCounts?: boolean;
};

export const WorkflowStatusDisplay = ({
  statusObject,
  assessmentSurvey,
  workflowDisplayType,
  isWorkflowActive,
}: {
  isWorkflowActive: boolean;
  statusObject: WorkflowDisplayStatus;
  assessmentSurvey: AssessmentSurvey | null;
  workflowDisplayType: string;
}) => {
  const theme = useTheme();
  const workflowMap = useWorkflowMap();

  if (!statusObject) return <span>{''}</span>;

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 70,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.5,
      }}
    >
      { workflowDisplayType && (
        <Typography
          sx={{
            color: theme.palette.customThemeColors?.grey,
            lineHeight: 1.2,
          }}
        >
          {workflowMap[workflowDisplayType]?.version || 'Unknown Version'}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 22,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.customThemeColors?.darkGrey,
            fontWeight: 500,
            lineHeight: 1.25,
          }}
        >
          {statusObject.status}
        </Typography>

        {statusObject.showRemediationCounts &&
          statusObject.remediationCounts &&
          assessmentSurvey && (
            <Box>
              {getRemediationCountsFromStatusObject({ statusObject })}
            </Box>
        )}
      </Box>

      {statusObject.answersCount > 0 && isWorkflowActive && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 14,
          }}
        >
          <Box sx={{ position: 'relative', flexGrow: 1 }}>
            <LinearProgress
              variant="determinate"
              color="primary"
              value={
                statusObject.questionsCount > 0
                  ? (statusObject.answersCount / statusObject.questionsCount) * 100
                  : 0
              }
              onClick={() => null}
              sx={{
                height: '7px',
                borderRadius: '1rem',
                cursor: 'pointer',
              }}
            />
          </Box>

          <Typography
            fontWeight="300"
            fontSize="12px"
            sx={{
              color: theme.palette.customThemeColors?.darkGrey,
              lineHeight: 1,
            }}
          >
            {`${((statusObject.answersCount / statusObject.questionsCount) * 100).toFixed(0)}%`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export function getWorkflowStatusForStep({
  workflow,
  company,
  survey,
  assessmentSurvey,
}: {
  workflow?: Workflow;
  company: Company;
  survey?: Survey;
  assessmentSurvey?: AssessmentSurvey;
}): string {
  const surveyStatusDisplay = surveyDisplayLogic({
    survey,
    companyCerts: company.companyCertifications,
    workflow,
  });

  // If JSX.Element(ie. Progress Bar) return default in-progress status
  const surveyDisplayLogicString = typeof surveyStatusDisplay === 'string' ? surveyStatusDisplay : MILESTONE_STATUS.QUESTIONNAIRE_IN_PROGRESS;

  const remediationDisplayStatus = () => {
    if (!assessmentSurvey) return '';
    const status = (assessmentSurvey.assessorAnswers as AssessorAnswer[]).some(it => ['remediation'].indexOf(it?.status || '') > -1 && !it.vendorStatus)
      ? MILESTONE_STATUS.ASSESSMENT_PENDING_REMEDIATION_PLANS
      : MILESTONE_STATUS.REMEDIATION_PLANS_SUBMITTED;
    return status;
  };

  const hasRemediationCounts = Boolean(workflow?.status === 'Remediation' || (workflow?.status === 'Complete' && assessmentSurvey?.bestPracticeRemediationCount));

  return !workflow?.assessmentSurvey
    ? surveyDisplayLogicString
    : hasRemediationCounts
      ? remediationDisplayStatus()
      : assessmentDisplayLogic(workflow.status, (workflow.badges ?? []) as Badge[]);
}

const WorkflowStatus = (props: WorkflowStatusProps) => {
  const theme = useTheme();
  const { workflow, company, survey, assessmentSurvey, hasBlueExpired, hasGoldExpired, hideBallCounts = false } = props;
  // redux
  const { user } = useAppSelector((state) => state.user);

  const { hasNextGenGold, hasGoldStar } = useMemo(() => {
    const result = { hasNextGenGold: false, hasGoldStar: false };

    if (!workflow?.badges?.length) return result;

    for (const badge of workflow.badges as Badge[]) {
      if (typeof badge === 'number') continue;

      if (badge.title === GOLD_NEXT_GEN_BADGE_LABEL) {
        result.hasNextGenGold = true;
      } else if (badge.title === GOLD_STAR_BADGE_LABEL) {
        result.hasGoldStar = true;
      }

      if (result.hasNextGenGold && result.hasGoldStar) break;
    }

    return result;
  }, [workflow?.badges]);

  const surveyStatusDisplay = useMemo(() => surveyDisplayLogic({
    survey,
    companyCerts: company.companyCertifications,
    workflow,
    hideProgressBar: hideBallCounts,
  }), [survey, company, workflow]);

  const showRemediationCounts = useMemo(() => {
    return workflow?.status === 'Remediation' || (workflow?.status === 'Complete' && (assessmentSurvey?.bestPracticeRemediationCount || assessmentSurvey?.implementationGuidanceRemediationCount)) && user?.type !== USER_TYPES.ASSESSOR;
  }, [workflow?.status, assessmentSurvey?.bestPracticeRemediationCount, user?.type]);

  const remediationDisplayStatus = useMemo(() => {
    if (!assessmentSurvey) return '';

    if (hasGoldStar) return MILESTONE_STATUS.ALL_REMEDIATIONS_COMPLETED;
    if (hasNextGenGold) return MILESTONE_STATUS.BEST_PRACTICE_REMEDIATIONS_COMPLETED;

    const status = (assessmentSurvey.assessorAnswers as AssessorAnswer[]).some(it => ['remediation'].indexOf(it?.status || '') > -1 && !it.vendorStatus)
      ? MILESTONE_STATUS.ASSESSMENT_PENDING_REMEDIATION_PLANS
      : MILESTONE_STATUS.REMEDIATION_PLANS_SUBMITTED;
    return status;
  }, [assessmentSurvey, hasNextGenGold, hasGoldStar]);

  const displayStatus = useMemo(() => {
    if (workflow?.assessmentSurvey) {
      if (showRemediationCounts) {
        return remediationDisplayStatus;
      }
      return assessmentDisplayLogic(workflow?.status, workflow.badges as Badge[]);
    }
    return surveyStatusDisplay;
  }, [workflow, showRemediationCounts, remediationDisplayStatus, surveyStatusDisplay]);

  return (
    <>
      {!!workflow?.assessmentSurvey && workflow?.isActive && (
        <AssessmentSurveyAuditLogs assessmentSurveyId={workflow.assessmentSurvey} status={workflow.status} />
      )}
      <Typography sx={{ color: theme.palette.customThemeColors?.darkGrey }}>
        {displayStatus}
      </Typography>
      {Boolean(workflow?.assessmentSurvey && showRemediationCounts && !hideBallCounts) &&
        getRemediationCounts({ assessmentSurvey })
      }
      {hasGoldExpired && (
        <Typography sx={{ color: theme.palette.customThemeColors?.darkGrey }}>Assessment Expired</Typography>
      )}
      {!hasGoldExpired && hasBlueExpired && (
        <Typography sx={{ color: theme.palette.customThemeColors?.darkGrey }}>Questionnaire Expired</Typography>
      )}
    </>
  );
};

export default WorkflowStatus;
